/**
 * Routes. Pages live in web/src/pages/<area>/ and are lazy-loaded so each area
 * is its own chunk. Add a route here AND a nav entry in shell/nav.ts.
 */

import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/shell/AppShell';
import LoginPage from '@/pages/LoginPage';

const BillingOverview = lazy(() => import('@/pages/billing/OverviewPage'));
const Merchants = lazy(() => import('@/pages/billing/MerchantsPage'));
const Runs = lazy(() => import('@/pages/billing/RunsPage'));
const Exceptions = lazy(() => import('@/pages/billing/ExceptionsPage'));
const Approvals = lazy(() => import('@/pages/billing/ApprovalsPage'));
const Leakage = lazy(() => import('@/pages/billing/LeakagePage'));
const CollectionsOverview = lazy(() => import('@/pages/collections/OverviewPage'));
const Worklist = lazy(() => import('@/pages/collections/WorklistPage'));
const Invoices = lazy(() => import('@/pages/collections/InvoicesPage'));
const AccountDetail = lazy(() => import('@/pages/collections/AccountPage'));
const CollectionsSettings = lazy(() => import('@/pages/collections/SettingsPage'));
const Outbox = lazy(() => import('@/pages/collections/OutboxPage'));
const PortalAdmin = lazy(() => import('@/pages/portal/PortalPage'));
const Audit = lazy(() => import('@/pages/audit/AuditPage'));
const MigrationOverview = lazy(() => import('@/pages/migration/OverviewPage'));
const MigrationCustomers = lazy(() => import('@/pages/migration/CustomersPage'));
const MigrationExceptions = lazy(() => import('@/pages/migration/ExceptionsPage'));
const MigrationSends = lazy(() => import('@/pages/migration/SendsPage'));
const MigrationNotices = lazy(() => import('@/pages/migration/NoticesPage'));
const MigrationPilot = lazy(() => import('@/pages/migration/PilotPage'));
const MigrationInvoicePreview = lazy(() => import('@/pages/migration/InvoicePreviewPage'));

/** The Stripe migration section is admin only (the BFF answers 403 to anyone else). */
const adminOnly = (el: JSX.Element) => <RequireAuth role="admin">{el}</RequireAuth>;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/billing" replace />} />
        <Route path="billing" element={<BillingOverview />} />
        <Route path="billing/merchants/*" element={<Merchants />} />
        <Route path="billing/runs/*" element={<Runs />} />
        <Route path="billing/exceptions" element={<Exceptions />} />
        <Route path="billing/approvals" element={<Approvals />} />
        <Route path="billing/leakage" element={<Leakage />} />
        <Route path="collections" element={<CollectionsOverview />} />
        <Route path="collections/worklist" element={<Worklist />} />
        <Route path="collections/invoices" element={<Invoices />} />
        <Route path="collections/accounts/:id" element={<AccountDetail />} />
        <Route
          path="collections/outbox"
          element={
            <RequireAuth role="operator">
              <Outbox />
            </RequireAuth>
          }
        />
        <Route
          path="collections/settings"
          element={
            <RequireAuth role="admin">
              <CollectionsSettings />
            </RequireAuth>
          }
        />
        <Route path="portal" element={<PortalAdmin />} />
        <Route path="audit" element={<Audit />} />
        <Route path="migration" element={adminOnly(<MigrationOverview />)} />
        <Route path="migration/customers" element={adminOnly(<MigrationCustomers />)} />
        <Route path="migration/customers/:id/invoice-preview" element={adminOnly(<MigrationInvoicePreview />)} />
        <Route path="migration/exceptions" element={adminOnly(<MigrationExceptions />)} />
        <Route path="migration/sends" element={adminOnly(<MigrationSends />)} />
        <Route path="migration/notices" element={adminOnly(<MigrationNotices />)} />
        <Route path="migration/pilot" element={adminOnly(<MigrationPilot />)} />
        <Route path="*" element={<Navigate to="/billing" replace />} />
      </Route>
    </Routes>
  );
}
